const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

export type SearchEntity = 'cxo' | 'brief' | 'article' | 'all';
export type SortBy = 'relevance' | 'newest' | 'oldest' | 'rating' | 'rate' | 'views';

export interface SearchFilters {
  industries?: string[];
  expertise?: string[];
  functions?: string[];
  minRate?: number;
  maxRate?: number;
  location?: string;
  availability?: string;
  engagementType?: string;
}

export interface SearchResult {
  id: string;
  type: SearchEntity;
  title: string;
  subtitle?: string;
  description?: string;
  imageUrl?: string;
  meta: Record<string, any>;
  score: number;
}

export interface SearchResponse {
  results: SearchResult[];
  total: number;
  page: number;
  totalPages: number;
  facets: {
    industries: Array<{ value: string; count: number }>;
    expertise: Array<{ value: string; count: number }>;
    functions: Array<{ value: string; count: number }>;
    locations: Array<{ value: string; count: number }>;
    types: Array<{ value: string; count: number }>;
  };
}

async function apiFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('token');
  const url = `${API_BASE_URL}${endpoint}`;
  
  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    credentials: 'include',
    ...options,
  };

  const response = await fetch(url, config);
  return response.json();
}

class SearchApiService {
  // Universal search
  async search(
    query: string,
    entity: SearchEntity = 'all',
    filters?: SearchFilters,
    sortBy: SortBy = 'relevance',
    page: number = 1,
    limit: number = 20
  ): Promise<{ success: boolean; data?: SearchResponse; message?: string }> {
    const params = new URLSearchParams();
    params.append('q', query);
    params.append('entity', entity);
    params.append('sortBy', sortBy);
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    if (filters?.industries?.length) params.append('industries', filters.industries.join(','));
    if (filters?.expertise?.length) params.append('expertise', filters.expertise.join(','));
    if (filters?.functions?.length) params.append('functions', filters.functions.join(','));
    if (filters?.minRate) params.append('minRate', filters.minRate.toString());
    if (filters?.maxRate) params.append('maxRate', filters.maxRate.toString());
    if (filters?.location) params.append('location', filters.location);
    if (filters?.availability) params.append('availability', filters.availability);
    if (filters?.engagementType) params.append('engagementType', filters.engagementType);

    return apiFetch(`/v1/search?${params.toString()}`);
  }

  // Search CXOs
  async searchCXOs(
    query: string,
    filters?: SearchFilters,
    sortBy: SortBy = 'relevance',
    page: number = 1,
    limit: number = 20
  ): Promise<{ success: boolean; data?: SearchResponse; message?: string }> {
    const params = new URLSearchParams();
    params.append('q', query);
    params.append('sortBy', sortBy);
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    if (filters?.industries?.length) params.append('industries', filters.industries.join(','));
    if (filters?.expertise?.length) params.append('expertise', filters.expertise.join(','));
    if (filters?.functions?.length) params.append('functions', filters.functions.join(','));
    if (filters?.minRate) params.append('minRate', filters.minRate.toString());
    if (filters?.maxRate) params.append('maxRate', filters.maxRate.toString());
    if (filters?.location) params.append('location', filters.location);
    if (filters?.availability) params.append('availability', filters.availability);

    return apiFetch(`/v1/search/cxos?${params.toString()}`);
  }

  // Search Briefs
  async searchBriefs(
    query: string,
    filters?: SearchFilters,
    sortBy: SortBy = 'newest',
    page: number = 1,
    limit: number = 20
  ): Promise<{ success: boolean; data?: SearchResponse; message?: string }> {
    const params = new URLSearchParams();
    params.append('q', query);
    params.append('sortBy', sortBy);
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    if (filters?.industries?.length) params.append('industries', filters.industries.join(','));
    if (filters?.functions?.length) params.append('functions', filters.functions.join(','));
    if (filters?.engagementType) params.append('engagementType', filters.engagementType);

    return apiFetch(`/v1/search/briefs?${params.toString()}`);
  }

  // Search Articles
  async searchArticles(
    query: string,
    sortBy: SortBy = 'newest',
    page: number = 1,
    limit: number = 20
  ): Promise<{ success: boolean; data?: SearchResponse; message?: string }> {
    const params = new URLSearchParams();
    params.append('q', query);
    params.append('sortBy', sortBy);
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    return apiFetch(`/v1/search/articles?${params.toString()}`);
  }

  // Get suggestions/autocomplete
  async getSuggestions(query: string, limit: number = 10): Promise<{ success: boolean; data: Array<{ text: string; type: string }> }> {
    const params = new URLSearchParams();
    params.append('q', query);
    params.append('limit', limit.toString());

    return apiFetch(`/v1/search/suggestions?${params.toString()}`);
  }

  // Get trending searches
  async getTrendingSearches(limit: number = 10): Promise<{ success: boolean; data: string[] }> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());

    return apiFetch(`/v1/search/trending?${params.toString()}`);
  }

  // Get facets/filter options
  async getFacets(entity: SearchEntity = 'all'): Promise<{ success: boolean; data: SearchResponse['facets'] }> {
    const params = new URLSearchParams();
    params.append('entity', entity);

    return apiFetch(`/v1/search/facets?${params.toString()}`);
  }
}

export const searchApi = new SearchApiService();
export default searchApi;
